import React, { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { Product, fetchProductById, calculateDiscountedPrice, hasDiscount, isProductOutOfStock } from '../../Services/productService';
import ReserveCarModal from '../Reservation/ReserveCarModal';
import LoanCalculatorModal from '../Loan-Calculator/LoanCalculatorModal';
import { submitReview, fetchProductReviews, Review } from '../../Services/reviewService';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useCompany } from '../../Contexts/CompanyContext';
import { useBrands } from '../../Contexts/BrandContext';
import HeaderSpacer from '../Header/HeaderSpacer';
import {
  Calendar, Gauge, Fuel, Settings2, Cog, Compass, Users,
  PaintBucket, Palette, ShieldCheck, UserCheck, Globe,
  BadgeCheck, Fingerprint, IdCard, MapPin, Tag,
} from 'lucide-react';

/* ================= IMAGE WITH PLACEHOLDER ================= */
interface ProductImageProps {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  className?: string;
  onExpandClick?: (resolvedSrc: string) => void;
}

const PLACEHOLDER = '/asset/images/product-placeholder.png';

const ImageWithPlaceholder: React.FC<ProductImageProps> = ({ src, alt, style, className, onExpandClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [src]);

  const displaySrc = error ? PLACEHOLDER : src;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Placeholder sits in normal flow — gives the container its height */}
      <img
        src={PLACEHOLDER}
        alt="Loading..."
        className={className}
        style={{
          ...(style || {}),
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: loaded ? 'none' : 'block',
        }}
      />

      {/* Real image loads off-screen (zero size, invisible) until onLoad fires */}
      <img
        src={displaySrc}
        alt={alt}
        className={className}
        style={{
          ...(style || {}),
          ...(loaded
            ? { width: '100%', height: '100%', objectFit: 'cover', display: 'block' }
            : { position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }),
        }}
        onLoad={() => setLoaded(true)}
        onError={() => { setError(true); setLoaded(true); }}
      />

      {onExpandClick && loaded && (
        <button
          className="flex-c-m size-108 how-pos1 bor0 fs-16 cl10 bg0 hov-btn3 trans-04"
          onClick={() => onExpandClick(error ? PLACEHOLDER : src)}
        >
          <i className="fa fa-expand"></i>
        </button>
      )}
    </div>
  );
};

/* ================= VEHICLE SPEC ROW ================= */
interface SpecRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const SpecRow: React.FC<SpecRowProps> = ({ icon, label, value }) => (
  <tr>
    <td className="stext-106 cl6" style={{ paddingRight: 20 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {icon}
        {label}
      </span>
    </td>
    <td className="stext-102 cl2">{value}</td>
  </tr>
);

/* ================= RICH TEXT DESCRIPTION RENDERER ================= */
interface RichDescriptionProps {
  html: string;
}

const RichDescription: React.FC<RichDescriptionProps> = ({ html }) => {
  const isPlainText = !/<[a-z][\s\S]*>/i.test(html);

  if (!html || html.trim() === '') {
    return (
      <p className="stext-102 cl6">No description available.</p>
    );
  }

  if (isPlainText) {
    return <p className="stext-102 cl6">{html}</p>;
  }

  return (
    <div
      className="rich-description"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const ProductDetails: React.FC = () => {
  const [product, setProduct] = useState<Product | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [newReview, setNewReview] = useState({
    rating: 0,
    comment: '',
    userName: '',
    userEmail: ''
  });
  const [reviewImage, setReviewImage] = useState<File | null>(null);
  const [reviewImagePreview, setReviewImagePreview] = useState<string>('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [showLoanCalculator, setShowLoanCalculator] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string>('');

  const mainSliderRef = useRef<Slider>(null);
  const thumbSliderRef = useRef<Slider>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatPrice, socialMedia, displayPrices } = useCompany();
  const { allBrands } = useBrands();

  // Set only when arriving from a grouped listing card (see
  // groupProductsForListing in productService.ts) — several interchangeable
  // brand-new units were collapsed into the card the customer clicked.
  // Absent on a direct link/refresh/share, in which case no count is shown.
  const availableCount = (location.state as { availableCount?: number } | null)?.availableCount;

  // ✅ FIX: Lock body scroll when lightbox is open to prevent layout shift
  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = scrollbarWidth + 'px';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [lightboxOpen]);

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) {
        navigate('/shop');
        return;
      }

      try {
        setLoading(true);
        const productData = await fetchProductById(id);

        if (!productData) {
          navigate('/shop');
          return;
        }

        setProduct(productData);

        const productReviews = await fetchProductReviews(id);
        setReviews(productReviews);
      } catch (error) {
        console.error('Error loading product:', error);
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  const getUserInitials = (name: string): string => {
    if (!name) return 'U';
    const words = name.trim().split(' ');
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const mainSliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    beforeChange: (_: number, next: number) => {
      setCurrentSlide(next);
      thumbSliderRef.current?.slickGoTo(next);
    },
    prevArrow: <button className="arrow-slick3 prev-slick3" type="button"><i className="fa fa-angle-left"></i></button>,
    nextArrow: <button className="arrow-slick3 next-slick3" type="button"><i className="fa fa-angle-right"></i></button>
  };

  const thumbSliderSettings = {
    dots: false,
    infinite: product?.images && product.images.length > 3,
    speed: 500,
    slidesToShow: Math.min(3, product?.images?.length || 1),
    slidesToScroll: 1,
    arrows: false,
    vertical: true,
    verticalSwiping: true,
    focusOnSelect: false
  };

  const handleThumbClick = (index: number) => {
    mainSliderRef.current?.slickGoTo(index);
    setCurrentSlide(index);
  };

  const handleImageClick = (img: string) => {
    setLightboxImage(img);
    setLightboxOpen(true);
  };

  const handleReviewImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReviewImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReviewImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!product?.id) return;

    if (!newReview.userName.trim() || !newReview.userEmail.trim() || !newReview.comment.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (newReview.rating < 1 || newReview.rating > 5) {
      alert('Please select a valid rating');
      return;
    }

    setReviewLoading(true);

    try {
      const result = await submitReview({
        productId: product.id,
        userId: 'anonymous',
        userName: newReview.userName,
        userEmail: newReview.userEmail,
        rating: newReview.rating,
        comment: newReview.comment
      }, reviewImage || undefined);

      if (result.success) {
        alert('Review submitted successfully!');

        setNewReview({
          rating: 5,
          comment: '',
          userName: '',
          userEmail: ''
        });
        setReviewImage(null);
        setReviewImagePreview('');

        const updatedReviews = await fetchProductReviews(product.id);
        setReviews(updatedReviews);
        setActiveTab('reviews');
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`zmdi ${star <= rating ? 'zmdi-star' : 'zmdi-star-outline'} cl11`}
            style={{ marginRight: '2px' }}
          ></i>
        ))}
      </div>
    );
  };

  // ✅ FIX: Loading state has min-height so footer stays at the bottom
  if (loading) {
    return (
      <div>
        <Header />
      <HeaderSpacer />
        <div className="container p-t-60 p-b-60" style={{ minHeight: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p>Loading product...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // ✅ FIX: Error/not-found state also has min-height
  if (!product) {
    return (
      <div>
        <Header />
      <HeaderSpacer />
        <div className="container p-t-60 p-b-60" style={{ minHeight: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <p>Product not found</p>
          <button onClick={() => navigate('/shop')} className="btn btn-primary">
            Back to Shop
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const productImages = product.images && product.images.length > 0
    ? product.images
    : ['asset/images/product-placeholder.png'];

  const soldOut = isProductOutOfStock(product);

  const basePrice      = product.price;
  const discount       = product.discount;
  const discountedPrice = calculateDiscountedPrice(basePrice, discount);
  const showDiscount   = hasDiscount(discount);

  // Resolve brand name from brands list
  const brandName = product.brandId
    ? allBrands?.find(b => b.id === product.brandId)?.name ?? null
    : null;

  return (
    <div>
      <Header />
      <HeaderSpacer />

      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>

          <a href="/shop" className="stext-109 cl8 hov-cl1 trans-04">
            Shop
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>

          <span className="stext-109 cl4">
            {product.name}
          </span>
        </div>
      </div>

      <section className="sec-product-detail bg0 p-t-65 p-b-60">
        <div className="container">
          <div className="row">
            <div className="col-md-6 col-lg-7 p-b-30">
              <div className="p-l-25 p-r-30 p-lr-0-lg">
                <div className="wrap-slick3 flex-sb flex-w">
                  <div className="wrap-slick3-dots">
                    <Slider ref={thumbSliderRef} {...thumbSliderSettings}>
                      {productImages.map((img, idx) => (
                        <div key={idx}>
                          <div
                            style={{ position: 'relative', marginBottom: '27px', cursor: 'pointer' }}
                            onClick={() => handleThumbClick(idx)}
                          >
                            <ImageWithPlaceholder
                              src={img}
                              alt={`Thumb ${idx + 1}`}
                              style={{ width: '100%' }}
                            />
                            <div
                              className={`slick3-dot-overlay ${currentSlide === idx ? 'active' : ''}`}
                              style={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                top: 0,
                                left: 0,
                                border: currentSlide === idx ? '2px solid #717fe0' : '2px solid transparent',
                                transition: 'all 0.4s'
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>

                  <div className="wrap-slick3-arrows flex-sb-m flex-w">
                    <Slider ref={mainSliderRef} {...mainSliderSettings} className="slick3 gallery-lb">
                      {productImages.map((img, idx) => (
                        <div key={idx} className="item-slick3" data-thumb={img}>
                          <div className="wrap-pic-w pos-relative">
                            <ImageWithPlaceholder
                              src={img}
                              alt="IMG-PRODUCT"
                              onExpandClick={handleImageClick}
                            />
                          </div>
                        </div>
                      ))}
                    </Slider>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-5 p-b-30">
              <div className="p-r-50 p-t-5 p-lr-0-lg">
                <h4 className="mtext-105 cl2 js-name-detail p-b-14">
                  {product.name}
                </h4>

                {displayPrices ? (
                  showDiscount ? (
                    <div className="detail-price-wrapper">
                      <span className="mtext-106 cl2 detail-price-discounted">
                        {formatPrice(discountedPrice)}
                      </span>
                      <span className="detail-price-original">
                        {formatPrice(basePrice)}
                      </span>
                      <span className="detail-discount-badge">
                        -{discount}%
                      </span>
                    </div>
                  ) : (
                    <span className="mtext-106 cl2">
                      {formatPrice(basePrice)}
                    </span>
                  )
                ) : (
                  <span className="mtext-106 cl2">Contact us for price</span>
                )}

                {/* ── Brand label ── */}
                {brandName && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{
                      display: 'inline-block',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '1.4px',
                      textTransform: 'uppercase',
                      color: '#717fe0',
                      lineHeight: 1.4
                    }}>
                      {brandName}
                    </span>
                  </div>
                )}

                {soldOut && (
                  <div style={{ marginTop: '10px' }}>
                    <span className="stext-102" style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                      {product.status || 'Not Available'}
                    </span>
                  </div>
                )}

                {!soldOut && !!availableCount && availableCount > 1 && (
                  <div style={{ marginTop: '10px' }}>
                    <span className="stext-102" style={{ color: '#2f9e44', fontWeight: 'bold' }}>
                      {availableCount} identical brand-new units available
                    </span>
                  </div>
                )}

                {product.trimName && (
                  <div style={{ marginTop: '10px' }}>
                    <span className="stext-106 cl6">Trim: </span>
                    <span className="stext-102 cl2">{product.trimName}</span>
                  </div>
                )}

                {product.branchName && (
                  <div style={{ marginTop: '6px' }}>
                    <span className="stext-106 cl6">Available at: </span>
                    <span className="stext-102 cl2">{product.branchName}</span>
                  </div>
                )}

                <div className="p-t-33">
                  <div className="flex-w flex-r-m p-b-10" style={{ gap: 12 }}>
                    <button
                      className="flex-c-m stext-101 cl0 size-101 bg1 bor1 hov-btn1 p-lr-15 trans-04 js-addcart-detail"
                      onClick={() => setShowReserveModal(true)}
                      disabled={soldOut}
                      style={{
                        flex: '1 1 0',
                        width: 'auto',
                        opacity: soldOut ? 0.5 : 1,
                        cursor: soldOut ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {soldOut ? product.status : 'Enquire / Reserve This Car'}
                    </button>
                    {displayPrices && (
                      <button
                        className="flex-c-m stext-101 cl2 size-101 bg0 bor1 hov-btn3 p-lr-15 trans-04"
                        onClick={() => setShowLoanCalculator(true)}
                        style={{ flex: '1 1 0', width: 'auto' }}
                      >
                        Calculate Loan
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-w flex-m p-l-100 p-t-40 respon7">
                  <div className="flex-m bor9 p-r-10 m-r-11">
                    <span>Follow us on</span>
                    {/* <a
                      href="#"
                      className="fs-14 cl3 hov-cl1 trans-04 lh-10 p-lr-5 p-tb-2 js-addwish-detail tooltip100"
                      data-tooltip="Add to Wishlist"
                    >
                      <i className="zmdi zmdi-favorite"></i>
                    </a> */}
                  </div>

                  {socialMedia?.facebook?.trim() && (
                    <a
                      href={socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fs-14 cl3 hov-cl1 trans-04 lh-10 p-lr-5 p-tb-2 m-r-8 tooltip100"
                      data-tooltip="Facebook"
                    >
                      <i className="fa fa-facebook"></i>
                    </a>
                  )}

                  {socialMedia?.instagram?.trim() && (
                    <a
                      href={socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fs-14 cl3 hov-cl1 trans-04 lh-10 p-lr-5 p-tb-2 m-r-8 tooltip100"
                      data-tooltip="Instagram"
                    >
                      <i className="fa fa-instagram"></i>
                    </a>
                  )}

                  {socialMedia?.threads?.trim() && (
                    <a
                      href={socialMedia.threads}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fs-14 cl3 hov-cl1 trans-04 lh-10 p-lr-5 p-tb-2 m-r-8 tooltip100"
                      data-tooltip="Threads"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448 512"
                        width="14"
                        height="14"
                        fill="currentColor"
                        style={{ verticalAlign: "middle" }}
                      >
                        <path d="M331.5 235.7c2.2 .9 4.2 1.9 6.3 2.8 29.2 14.1 50.6 35.2 61.8 61.4 15.7 36.5 17.2 95.8-30.3 143.2-36.2 36.2-80.3 52.5-142.6 53l-.3 0c-70.2-.5-124.1-24.1-160.4-70.2-32.3-41-48.9-98.1-49.5-169.6l0-.5C17 184.3 33.6 127.2 65.9 86.2 102.2 40.1 156.2 16.5 226.4 16l.3 0c70.3 .5 124.9 24 162.3 69.9 18.4 22.7 32 50 40.6 81.7l-40.4 10.8c-7.1-25.8-17.8-47.8-32.2-65.4-29.2-35.8-73-54.2-130.5-54.6-57 .5-100.1 18.8-128.2 54.4-26.2 33.3-39.8 81.5-40.3 143.2 .5 61.7 14.1 109.9 40.3 143.3 28 35.6 71.2 53.9 128.2 54.4 51.4-.4 85.4-12.6 113.7-40.9 32.3-32.2 31.7-71.8 21.4-95.9-6.1-14.2-17.1-26-31.9-34.9-3.7 26.9-11.8 48.3-24.7 64.8-17.1 21.8-41.4 33.6-72.7 35.3-23.6 1.3-46.3-4.4-63.9-16-20.8-13.8-33-34.8-34.3-59.3-2.5-48.3 35.7-83 95.2-86.4 21.1-1.2 40.9-.3 59.2 2.8-2.4-14.8-7.3-26.6-14.6-35.2-10-11.7-25.6-17.7-46.2-17.8l-.7 0c-16.6 0-39 4.6-53.3 26.3l-34.4-23.6c19.2-29.1 50.3-45.1 87.8-45.1l.8 0c62.6 .4 99.9 39.5 103.7 107.7l-.2 .2 .1 0zm-156 68.8c1.3 25.1 28.4 36.8 54.6 35.3 25.6-1.4 54.6-11.4 59.5-73.2-13.2-2.9-27.8-4.4-43.4-4.4-4.8 0-9.6 .1-14.4 .4-42.9 2.4-57.2 23.2-56.2 41.8l-.1 .1z" />
                      </svg>
                    </a>
                  )}
                </div>

              </div>
            </div>
          </div>

          <div className="bor10 m-t-50 p-t-43 p-b-40">
            <div className="tab01">
              <ul className="nav nav-tabs" role="tablist">
                <li className="nav-item p-b-10">
                  <button
                    className={`nav-link ${activeTab === 'description' ? 'active' : ''}`}
                    onClick={() => setActiveTab('description')}
                  >
                    Description
                  </button>
                </li>

                <li className="nav-item p-b-10">
                  <button
                    className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
                    onClick={() => setActiveTab('reviews')}
                  >
                    Reviews ({reviews.length})
                  </button>
                </li>

                <li className="nav-item p-b-10">
                  <button
                    className={`nav-link ${activeTab === 'add-review' ? 'active' : ''}`}
                    onClick={() => setActiveTab('add-review')}
                  >
                    Add a Review
                  </button>
                </li>
              </ul>

              <div className="tab-content p-t-43">

                <div
                  className={`tab-pane fade ${activeTab === 'description' ? 'show active' : ''}`}
                  id="description"
                  role="tabpanel"
                >
                  <div className="how-pos2 p-lr-15-md">
                    <RichDescription html={product.description || ''} />
                  </div>

                  <div className="how-pos2 p-lr-15-md p-t-30">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 48, alignItems: 'flex-start' }}>
                    <div style={{ flex: '1 1 420px', minWidth: 0 }}>
                    <h4 className="mtext-105 cl2 p-b-15">Vehicle Specifications</h4>
                    <table className="table" style={{ maxWidth: 560 }}>
                      <tbody>
                        {product.modelYear && (
                          <SpecRow icon={<Calendar size={16} />} label="Model Year" value={product.modelYear} />
                        )}
                        {product.mileageKm != null && (
                          <SpecRow icon={<Gauge size={16} />} label="Mileage" value={`${product.mileageKm.toLocaleString()} km`} />
                        )}
                        {product.fuelType && (
                          <SpecRow icon={<Fuel size={16} />} label="Fuel Type" value={product.fuelType} />
                        )}
                        {product.transmission && (
                          <SpecRow icon={<Settings2 size={16} />} label="Transmission" value={product.transmission} />
                        )}
                        {product.engineCapacityCc != null && (
                          <SpecRow icon={<Cog size={16} />} label="Engine" value={`${product.engineCapacityCc} cc`} />
                        )}
                        {product.driveType && (
                          <SpecRow icon={<Compass size={16} />} label="Drive Type" value={product.driveType} />
                        )}
                        {product.seatingCapacity != null && (
                          <SpecRow icon={<Users size={16} />} label="Seating Capacity" value={product.seatingCapacity} />
                        )}
                        {product.exteriorColor && (
                          <SpecRow icon={<PaintBucket size={16} />} label="Exterior Color" value={product.exteriorColor} />
                        )}
                        {product.interiorColor && (
                          <SpecRow icon={<Palette size={16} />} label="Interior Color" value={product.interiorColor} />
                        )}
                        {product.condition && (
                          <SpecRow icon={<ShieldCheck size={16} />} label="Condition" value={product.condition} />
                        )}
                        {product.ownersCount != null && (
                          <SpecRow icon={<UserCheck size={16} />} label="Previous Owners" value={product.ownersCount} />
                        )}
                        {product.isImported && product.countryOfOrigin && (
                          <SpecRow icon={<Globe size={16} />} label="Imported From" value={product.countryOfOrigin} />
                        )}
                      </tbody>
                    </table>
                    </div>

                    <div
                      style={{
                        flex: '1 1 280px',
                        maxWidth: 360,
                        border: '1px solid #e6f4ea',
                        borderRadius: 12,
                        padding: '24px',
                        background: 'linear-gradient(180deg, #f3faf5 0%, #fafafa 100%)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
                        <BadgeCheck size={22} color="#2f9e44" />
                        <span className="mtext-105 cl2" style={{ fontSize: 16 }}>Dealer Verified</span>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <ShieldCheck size={17} color="#2f9e44" style={{ marginTop: 2, flexShrink: 0 }} />
                          <span className="stext-106 cl6">Physically inspected by our team before listing</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <IdCard size={17} color="#2f9e44" style={{ marginTop: 2, flexShrink: 0 }} />
                          <span className="stext-106 cl6">Registration &amp; ownership documents checked</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <Fingerprint size={17} color="#2f9e44" style={{ marginTop: 2, flexShrink: 0 }} />
                          <span className="stext-106 cl6">Full VIN/chassis on file — kept confidential and shared only with our sales team on request</span>
                        </div>
                      </div>

                      <div style={{ borderTop: '1px dashed #d7e8dc', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {product.registrationYear && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Calendar size={16} color="#717fe0" />
                            <span className="stext-106 cl6">Registered</span>
                            <span className="stext-102 cl2" style={{ marginLeft: 'auto', fontWeight: 600 }}>{product.registrationYear}</span>
                          </div>
                        )}
                        {product.branchName && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <MapPin size={16} color="#717fe0" />
                            <span className="stext-106 cl6">Location</span>
                            <span className="stext-102 cl2" style={{ marginLeft: 'auto', fontWeight: 600 }}>{product.branchName}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Tag size={16} color="#717fe0" />
                          <span className="stext-106 cl6">Reference No.</span>
                          <span className="stext-102 cl2" style={{ marginLeft: 'auto', fontWeight: 600 }}>
                            #{product.id.slice(-6).toUpperCase()}
                          </span>
                        </div>
                      </div>

                      <p className="stext-107 cl6" style={{ margin: '18px 0 0', lineHeight: 1.6 }}>
                        Quote the reference number above when you contact us to ask about this vehicle.
                      </p>
                    </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`tab-pane fade ${activeTab === 'reviews' ? 'show active' : ''}`}
                  id="reviews"
                  role="tabpanel"
                >
                  <div className="row">
                    <div className="col-sm-10 col-md-8 col-lg-6 m-lr-auto">
                      <div className="p-b-30 m-lr-15-sm">
                        {reviews.length === 0 ? (
                          <div className="text-center p-t-20 p-b-20">
                            <p className="stext-102 cl6">No reviews yet. Be the first to review this product!</p>
                          </div>
                        ) : (
                          reviews.map((review) => (
                            <div key={review.id} className="flex-w flex-t p-b-68">
                              <div className="wrap-pic-s size-109 bor0 of-hidden m-r-18 m-t-6">
                                {review.imageUrl ? (
                                  <img
                                    src={review.imageUrl}
                                    alt="Review Avatar"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const initialsDiv = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (initialsDiv) initialsDiv.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="user-initials-avatar"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: review.imageUrl ? 'none' : 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#717fe0',
                                    color: 'white',
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                    fontFamily: 'Poppins-Medium, sans-serif'
                                  }}
                                >
                                  {getUserInitials(review.userName)}
                                </div>
                              </div>

                              <div className="size-207">
                                <div className="flex-w flex-sb-m p-b-17">
                                  <span className="mtext-107 cl2 p-r-20">
                                    {review.userName}
                                  </span>

                                  <span className="fs-18 cl11">
                                    {renderStars(review.rating)}
                                  </span>
                                </div>

                                <p className="stext-102 cl6">
                                  {review.comment}
                                </p>

                                <div className="flex-w flex-sb-m p-t-10">
                                  <span className="stext-107 cl6">
                                    {review.createdAt?.toDate().toLocaleDateString() || 'Recently'}
                                  </span>
                                  <span className="stext-107 cl6">
                                    {review.userEmail}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className={`tab-pane fade ${activeTab === 'add-review' ? 'show active' : ''}`}
                  id="add-review"
                  role="tabpanel"
                >
                  <div className="row">
                    <div className="col-sm-10 col-md-8 col-lg-6 m-lr-auto">
                      <div className="p-b-30 m-lr-15-sm">
                        <form onSubmit={handleReviewSubmit} className="w-full">
                          <h5 className="mtext-108 cl2 p-b-7">
                            Add a review
                          </h5>

                          <p className="stext-102 cl6">
                            Your email address will not be published. Required fields are marked *
                          </p>

                          <div className="flex-w flex-m p-t-50 p-b-23">
                            <span className="stext-102 cl3 m-r-16">
                              Your Rating *
                            </span>

                            <div className="wrap-rating fs-18 cl11 pointer">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <i
                                  key={star}
                                  className={`item-rating pointer zmdi ${star <= newReview.rating ? 'zmdi-star' : 'zmdi-star-outline'}`}
                                  onClick={() => setNewReview({ ...newReview, rating: star })}
                                  style={{ cursor: 'pointer', marginRight: '5px' }}
                                ></i>
                              ))}
                            </div>
                          </div>

                          <div className="row p-b-25">
                            <div className="col-12 p-b-5">
                              <label className="stext-102 cl3">Your review *</label>
                              <textarea
                                className="size-110 bor8 stext-102 cl2 p-lr-20 p-tb-10"
                                value={newReview.comment}
                                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                                rows={4}
                                required
                              ></textarea>
                            </div>

                            <div className="col-sm-6 p-b-5">
                              <label className="stext-102 cl3">Name *</label>
                              <input
                                className="size-111 bor8 stext-102 cl2 p-lr-20"
                                type="text"
                                value={newReview.userName}
                                onChange={(e) => setNewReview({ ...newReview, userName: e.target.value })}
                                required
                              />
                            </div>

                            <div className="col-sm-6 p-b-5">
                              <label className="stext-102 cl3">Email *</label>
                              <input
                                className="size-111 bor8 stext-102 cl2 p-lr-20"
                                type="email"
                                value={newReview.userEmail}
                                onChange={(e) => setNewReview({ ...newReview, userEmail: e.target.value })}
                                required
                              />
                            </div>

                            <div className="col-12 p-t-10">
                              <label className="stext-102 cl3">Review Image (Optional)</label>
                              <div className="flex-w flex-m align-items-center">
                                <label className="review-image-upload">
                                  <input
                                    className="hidden"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleReviewImageChange}
                                  />
                                  <div className="size-111 bor8 stext-102 cl2 p-lr-20 p-tb-10 flex-c-m pointer bg-light-gray">
                                    <i className="zmdi zmdi-camera m-r-10"></i>
                                    Choose Image
                                  </div>
                                  {reviewImage && (
                                    <span className="stext-102 cl6 p-l-10">
                                      {reviewImage.name}
                                    </span>
                                  )}
                                </label>

                                {reviewImagePreview && (
                                  <div className="m-l-20 pos-relative">
                                    <div
                                      className="review-image-preview"
                                      style={{
                                        width: '120px',
                                        height: '120px',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        border: '1px solid #e6e6e6',
                                        position: 'relative'
                                      }}
                                    >
                                      <img
                                        src={reviewImagePreview}
                                        alt="Preview"
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover'
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setReviewImage(null);
                                          setReviewImagePreview('');
                                        }}
                                        style={{
                                          position: 'absolute',
                                          top: '5px',
                                          right: '5px',
                                          background: 'rgba(0,0,0,0.7)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '50%',
                                          width: '24px',
                                          height: '24px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          fontSize: '12px'
                                        }}
                                      >
                                        ×
                                      </button>
                                    </div>
                                    <p className="stext-102 cl6 p-t-5">
                                      Click the × button to remove
                                    </p>
                                  </div>
                                )}
                              </div>
                              <p className="stext-102 cl6 p-t-5">
                                Maximum file size: 5MB. Allowed formats: JPG, PNG, GIF.
                              </p>
                            </div>
                          </div>

                          <button
                            type="submit"
                            className="flex-c-m stext-101 cl0 size-112 bg7 bor11 hov-btn3 p-lr-15 trans-04 m-b-10"
                            disabled={reviewLoading}
                          >
                            {reviewLoading ? 'Submitting...' : 'Submit Review'}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {lightboxOpen && (
        <div className="image-lightbox">
          <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}></div>
          <div className="lightbox-content">
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)}>
              ×
            </button>
            <img
              src={lightboxImage}
              alt="Product"
              onError={(e) => { e.currentTarget.src = '/asset/images/product-placeholder.png'; }}
            />
          </div>
        </div>
      )}

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Detail page price styles ── */
        .detail-price-wrapper {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
        }
        .detail-price-discounted {
          font-weight: 600;
        }
        .detail-price-original {
          text-decoration: line-through;
          color: #aaa;
          font-size: 16px;
          font-weight: 400;
        }
        .detail-discount-badge {
          display: inline-block;
          background: #e74c3c;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.8px;
          padding: 3px 9px;
          border-radius: 2px;
          vertical-align: middle;
          line-height: 1.6;
        }

        /* ── Rich description styles ── */
        .rich-description {
          font-size: 14px;
          line-height: 1.8;
          color: #666;
          font-family: 'Poppins-Regular', sans-serif;
        }
        .rich-description p { margin: 0 0 12px; }
        .rich-description p:empty,
        .rich-description p br:only-child { margin: 0 0 6px; }
        .rich-description h1 { font-size: 24px; font-weight: 700; color: #333; margin: 0 0 12px; }
        .rich-description h2 { font-size: 20px; font-weight: 600; color: #333; margin: 0 0 10px; }
        .rich-description h3 { font-size: 17px; font-weight: 600; color: #444; margin: 0 0 8px; }
        .rich-description strong, .rich-description b { font-weight: 700; color: #444; }
        .rich-description em, .rich-description i { font-style: italic; }
        .rich-description u { text-decoration: underline; }
        .rich-description s, .rich-description strike { text-decoration: line-through; }
        .rich-description ul { list-style: disc; padding-left: 24px; margin: 0 0 12px; }
        .rich-description ol { list-style: decimal; padding-left: 24px; margin: 0 0 12px; }
        .rich-description li { margin-bottom: 4px; font-size: 14px; color: #666; line-height: 1.7; }
        .rich-description blockquote { border-left: 3px solid #717fe0; padding: 8px 16px; margin: 12px 0; color: #888; font-style: italic; background: #f9f9ff; }
        .rich-description pre { background: #f5f5f5; border-radius: 4px; padding: 12px 16px; font-size: 13px; font-family: monospace; overflow-x: auto; margin: 0 0 12px; color: #333; }
        .rich-description a { color: #717fe0; text-decoration: underline; transition: color 0.3s; }
        .rich-description a:hover { color: #555; }
        .rich-description table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 14px; }
        .rich-description table th { background: #f5f5f5; font-weight: 600; color: #444; border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
        .rich-description table td { border: 1px solid #ddd; padding: 8px 12px; color: #666; vertical-align: top; }
        .rich-description table tr:nth-child(even) td { background: #fafafa; }
        .rich-description img { max-width: 100%; height: auto; border-radius: 4px; margin: 8px 0; }

        /* ── Dropdown styles ── */
        .wrap-dropdown-content { position: relative; }
        .wrap-dropdown-content select { display: block; width: 100%; background: transparent; padding: 10px 15px; font-size: 13px; line-height: 1.4; color: #555; border: 1px solid #e6e6e6; border-radius: 3px; appearance: none; outline: none; cursor: pointer; font-family: 'Poppins-Regular', sans-serif; }
        .wrap-dropdown-content select:focus { border-color: #717fe0; }
        .wrap-dropdown-content::after { content: '▼'; position: absolute; right: 15px; top: 50%; transform: translateY(-50%); font-size: 10px; color: #666; pointer-events: none; }
        .wrap-dropdown-content select option:disabled { color: #ccc; background-color: #f5f5f5; }

        /* ── Review image upload ── */
        .review-image-upload { cursor: pointer; display: flex; align-items: center; }
        .review-image-upload input[type="file"] { display: none; }
        .review-image-upload .bg-light-gray { background-color: #f7f7f7; border: 1px dashed #ddd; transition: all 0.3s; }
        .review-image-upload .bg-light-gray:hover { background-color: #e6e6e6; border-color: #717fe0; }
        .review-image-preview { box-shadow: 0 2px 10px rgba(0,0,0,0.1); transition: transform 0.3s; }
        .review-image-preview:hover { transform: scale(1.05); }

        /* ── Lightbox ── */
        .image-lightbox {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .lightbox-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.9);
          cursor: pointer;
        }
        .lightbox-content {
          position: relative;
          z-index: 10000;
          max-width: 90%;
          max-height: 90%;
          animation: lightboxFadeIn 0.3s ease;
        }
        .lightbox-content img {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
          box-shadow: 0 5px 30px rgba(0, 0, 0, 0.5);
          border-radius: 4px;
        }
        .lightbox-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: none;
          border: none;
          color: white;
          font-size: 40px;
          cursor: pointer;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s;
        }
        .lightbox-close:hover { color: #717fe0; }

        @keyframes lightboxFadeIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }

        @media (max-width: 768px) {
          .review-image-upload { flex-direction: column; align-items: flex-start; }
          .review-image-preview { margin-left: 0; margin-top: 15px; }
          .lightbox-content { max-width: 95%; }
          .lightbox-close { top: -50px; right: -10px; }
        }

        .wrap-dropdown-content select::-webkit-scrollbar { width: 8px; }
        .wrap-dropdown-content select::-webkit-scrollbar-track { background: #f1f1f1; }
        .wrap-dropdown-content select::-webkit-scrollbar-thumb { background: #717fe0; border-radius: 4px; }
        .wrap-dropdown-content select::-webkit-scrollbar-thumb:hover { background: #555; }
      `}</style>

      {showReserveModal && product && (
        <ReserveCarModal
          carId={product.id}
          carName={product.name}
          carImage={product.images?.[0]}
          carPrice={product.price}
          onClose={() => setShowReserveModal(false)}
        />
      )}

      {showLoanCalculator && product && (
        <LoanCalculatorModal
          carName={product.name}
          carPrice={product.price}
          onClose={() => setShowLoanCalculator(false)}
        />
      )}
    </div>
  );
};

export default ProductDetails;